import React, { useMemo } from "react";
import { barX, barY, colorLegend, defineChart, group, lineY, stack } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";
import { COLORWAY } from "./chartTheme";

export interface CategoryBarRow {
    category: string;
    series: string;
    value: number;
}

export function CategoryBarChart({
    rows,
    orientation = "vertical",
    mode = "group",
    colors,
    showLegend = false,
    valueFormat,
    valueLabel,
    valueDomain,
    overlayLine,
    ariaLabel,
    onReady,
    height = 320,
    width,
    style,
    className,
    onSelect
}: {
    rows: CategoryBarRow[];
    orientation?: "vertical" | "horizontal";
    mode?: "group" | "stack";
    colors?: string[];
    showLegend?: boolean;
    valueFormat?: (value: number) => string;
    valueLabel?: string;
    valueDomain?: [number, number];
    overlayLine?: { name: string; color: string; values: number[] };
    ariaLabel: string;
    onReady?: () => void;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
    className?: string;
    onSelect?: (category: string) => void;
}): React.ReactElement {
    const categories = useMemo(() => {
        const seen: string[] = [];
        for (const row of rows) {
            if (!seen.includes(row.category)) seen.push(row.category);
        }
        return seen;
    }, [rows]);
    const seriesNames = useMemo(() => {
        const seen: string[] = [];
        for (const row of rows) {
            if (!seen.includes(row.series)) seen.push(row.series);
        }
        return seen;
    }, [rows]);
    const palette = colors && colors.length > 0 ? colors : COLORWAY;
    const definition = useMemo(() => {
        let layout = undefined;
        if (mode === "stack") {
            layout = stack();
        } else {
            // Group only when one category has several series. A unique series per
            // category (used for per-bar colors) would otherwise split the band
            // across every series and paint hairline bars.
            const seriesByCategory = new Map<string, string>();
            let multipleSeriesInCategory = false;
            for (const row of rows) {
                const existing = seriesByCategory.get(row.category);
                if (typeof existing !== "undefined" && existing !== row.series) {
                    multipleSeriesInCategory = true;
                    break;
                }
                seriesByCategory.set(row.category, row.series);
            }
            if (multipleSeriesInCategory) layout = group();
        }
        const valueScale = valueDomain ? scaleLinear().domain(valueDomain) : scaleLinear;
        const valueAxis = {
            scale: valueScale,
            nice: !valueDomain,
            grid: true,
            axis: {
                label: valueLabel,
                ticks: valueFormat ? { format: valueFormat } : undefined
            }
        };
        const categoryAxis = {
            scale: scaleBand<string>().domain(categories).padding(0.2),
            grid: false
        };
        const barOptions = {
            z: "series" as const,
            color: "series" as const,
            key: (row: CategoryBarRow) => row.category + "\u0000" + row.series,
            layout
        };
        const marks = [];
        if (orientation === "horizontal") {
            marks.push(barX(rows, { ...barOptions, y: "category", x: "value" }));
        } else {
            marks.push(barY(rows, { ...barOptions, x: "category", y: "value" }));
        }
        if (overlayLine && orientation === "vertical") {
            const lineRows = categories.map((category, index) => ({
                category,
                value: overlayLine.values[index] ?? 0,
                series: overlayLine.name
            }));
            marks.push(
                decorative(
                    lineY(lineRows, {
                        x: "category",
                        y: "value",
                        stroke: overlayLine.color,
                        strokeWidth: 2.5,
                        points: true
                    })
                )
            );
        }
        return defineChart({
            marks,
            scales:
                orientation === "horizontal"
                    ? { x: valueAxis, y: categoryAxis }
                    : { x: categoryAxis, y: valueAxis },
            color: {
                domain: overlayLine ? [...seriesNames, overlayLine.name] : seriesNames,
                range: [...seriesNames.map((_, index) => palette[index % palette.length]), overlayLine ? overlayLine.color : palette[0]],
                legend: showLegend ? colorLegend() : undefined
            },
            tooltip
        });
    }, [categories, mode, orientation, overlayLine, palette, rows, seriesNames, showLegend, valueDomain, valueFormat, valueLabel]);

    if (rows.length === 0) return <div />;

    return (
        <ChartFrame
            definition={definition}
            ariaLabel={ariaLabel}
            ariaDescription={categories.join(", ")}
            height={height}
            onReady={onReady}
            width={width}
            style={style}
            className={className}
            onSelect={onSelect ? (datum) => onSelect(String(datum?.category ?? "")) : undefined}
        />
    );
}

export function DualAxisBars({
    categories,
    left,
    right,
    ariaLabel,
    height = 320,
    width,
    style
}: {
    categories: string[];
    left: { name: string; values: number[]; color: string; label?: string; format?: (value: number) => string };
    right: { name: string; values: number[]; color: string; label?: string; format?: (value: number) => string; hidden?: boolean };
    ariaLabel: string;
    height?: number;
    width?: number;
    style?: React.CSSProperties;
}): React.ReactElement {
    const definition = useMemo(() => {
        const leftRows = categories.map((category, index) => ({ category, value: left.values[index] ?? 0, series: left.name }));
        const rightRows = categories.map((category, index) => ({ category, value: right.values[index] ?? 0, series: right.name }));
        return defineChart({
            marks: [
                barY(leftRows, { x: "category", y: "value", fill: left.color, key: "category" }),
                barY(rightRows, {
                    x: "category",
                    y: "value",
                    yScale: "y2",
                    fill: right.color,
                    fillOpacity: right.hidden ? 0 : 1,
                    key: (row) => row.category + "-right"
                })
            ],
            scales: {
                x: { scale: scaleBand<string>().domain(categories).padding(0.3), grid: false },
                y: {
                    scale: scaleLinear,
                    nice: true,
                    grid: true,
                    axis: { label: left.label, ticks: left.format ? { format: left.format } : undefined }
                },
                y2: {
                    scale: scaleLinear,
                    channel: "y",
                    side: "right",
                    nice: true,
                    grid: false,
                    axis: { label: right.label, ticks: right.format ? { format: right.format } : undefined }
                }
            },
            tooltip
        });
    }, [categories, left, right]);

    return <ChartFrame definition={definition} ariaLabel={ariaLabel} ariaDescription={categories.join(", ")} height={height} width={width} style={style} />;
}
